import { useCallback, useMemo, useState } from "react";

import { useLocalStorageState } from "./use-local-storage-state";

export type TableDensity = "normal" | "small";

/** Tela só com densidade: sem `allColumns`/`mainColumns`, sem colunas (identidade estável). */
const NO_COLUMNS: readonly never[] = [];

/** Regra de listagem (GLOSSARIO.md, "Listagens"): a seção "Mostrar e Ocultar Colunas" só existe com mais de 6 colunas. */
const COLUMNS_SECTION_MIN = 6;

/** Chave da densidade no `Storage` em memória da instância (sem `densityKey`): nunca chega ao `localStorage`. */
const IN_MEMORY_DENSITY_KEY = "density";

export interface UseTableSettingsOptions<C extends string> {
  /** Chave do `localStorage` da densidade; sem ela a densidade vale só em memória (tela que não persiste). */
  densityKey?: string;
  /** Chave do `localStorage` das colunas visíveis; sem ela as colunas valem só em memória. */
  columnsKey?: string;
  /** Todas as colunas, na ordem de exibição. Defina fora do render (identidade estável). */
  allColumns?: readonly C[];
  /** Colunas que nunca podem ser ocultadas (definidas na HU da tela). */
  mainColumns?: readonly C[];
  /** Densidade inicial. Padrão: `"normal"` (Confortável). */
  defaultDensity?: TableDensity;
}

export interface TableSettingsDraft<C extends string> {
  density: TableDensity;
  visibleColumns: C[];
}

export interface TableSettingsDialog<C extends string> {
  isOpen: boolean;
  /** Abre o dialog com o rascunho igual ao estado efetivo. */
  open: () => void;
  /** Fecha e descarta o rascunho (mesmo efeito de `cancel`). */
  close: () => void;
  /** Rascunho em edição; fora do dialog, igual ao estado efetivo. */
  draft: TableSettingsDraft<C>;
  setDraftDensity: (density: TableDensity) => void;
  /** Alterna a coluna no rascunho; colunas principais e desconhecidas são ignoradas. */
  toggleDraftColumn: (column: C) => void;
  /** Efetiva o rascunho e fecha. */
  apply: () => void;
  /** Descarta o rascunho e fecha. */
  cancel: () => void;
}

export interface UseTableSettingsResult<C extends string> {
  density: TableDensity;
  /** `density` no formato da prop `size` do `DataTable`. */
  primeSize: TableDensity;
  visibleColumns: C[];
  isColumnVisible: (column: C) => boolean;
  showColumnsSection: boolean;
  dialog: TableSettingsDialog<C>;
}

/** Valores gravados antes do hook (texto cru, sem JSON) por tela; viram a densidade do DS na leitura. */
const LEGACY_DENSITY: Record<string, TableDensity> = {
  confortavel: "normal",
  comfortable: "normal",
  compacto: "small",
  compact: "small",
};

function parseDensity(raw: string): TableDensity {
  const legacy = LEGACY_DENSITY[raw.replace(/^"|"$/g, "")];
  if (legacy) return legacy;
  const value: unknown = JSON.parse(raw);
  if (value === "normal" || value === "small") return value;
  throw new Error("Densidade inválida");
}

/** `Storage` em memória: sem `densityKey`/`columnsKey` a densidade e as colunas ficam no mesmo hook de persistência, mas nunca saem da instância. */
function createMemoryStorage(): Storage {
  const data = new Map<string, string>();
  return {
    get length() {
      return data.size;
    },
    clear: () => data.clear(),
    getItem: (key) => data.get(key) ?? null,
    key: (index) => Array.from(data.keys())[index] ?? null,
    removeItem: (key) => void data.delete(key),
    setItem: (key, value) => void data.set(key, value),
  };
}

/**
 * Densidade e colunas visíveis de uma tabela, com o rascunho do dialog "Configurações da Tabela":
 * só `apply` efetiva, `cancel` descarta. Sem UI (o dialog é um componente à parte).
 */
export function useTableSettings<C extends string>({
  densityKey,
  columnsKey,
  allColumns = NO_COLUMNS,
  mainColumns = NO_COLUMNS,
  defaultDensity = "normal",
}: UseTableSettingsOptions<C>): UseTableSettingsResult<C> {
  const [memoryStorage] = useState(createMemoryStorage);

  const [density, setDensity] = useLocalStorageState<TableDensity>(
    densityKey ?? IN_MEMORY_DENSITY_KEY,
    defaultDensity,
    { parse: parseDensity, ...(densityKey ? {} : { storage: memoryStorage }) },
  );

  /** Ocultas válidas: só colunas conhecidas e nunca as principais. */
  const normalizeHidden = useCallback(
    (columns: readonly string[]): C[] =>
      allColumns.filter((column) => !mainColumns.includes(column) && columns.includes(column)),
    [allColumns, mainColumns],
  );

  // Persiste as colunas OCULTAS: uma coluna criada depois aparece por padrão e remover uma coluna não quebra a preferência salva.
  const [storedHidden, setHidden] = useLocalStorageState<C[]>(
    columnsKey ?? "ds:table-settings:hidden-columns",
    () => [],
    {
      parse: (raw) => {
        const value: unknown = JSON.parse(raw);
        if (!Array.isArray(value) || !value.every((item) => typeof item === "string")) {
          throw new Error("Colunas inválidas");
        }
        return normalizeHidden(value);
      },
      ...(columnsKey ? {} : { storage: memoryStorage }),
    },
  );
  const visibleColumns = useMemo(() => {
    const hidden = normalizeHidden(storedHidden);
    return allColumns.filter((column) => !hidden.includes(column));
  }, [allColumns, normalizeHidden, storedHidden]);

  const [draftState, setDraftState] = useState<TableSettingsDraft<C> | null>(null);

  const open = useCallback(
    () => setDraftState({ density, visibleColumns }),
    [density, visibleColumns],
  );
  const cancel = useCallback(() => setDraftState(null), []);

  const setDraftDensity = useCallback((next: TableDensity) => {
    setDraftState((current) => (current ? { ...current, density: next } : current));
  }, []);

  const toggleDraftColumn = useCallback(
    (column: C) => {
      if (mainColumns.includes(column) || !allColumns.includes(column)) return;
      setDraftState((current) => {
        if (!current) return current;
        const next = current.visibleColumns.includes(column)
          ? current.visibleColumns.filter((item) => item !== column)
          : [...current.visibleColumns, column];
        return { ...current, visibleColumns: allColumns.filter((item) => next.includes(item)) };
      });
    },
    [allColumns, mainColumns],
  );

  const apply = useCallback(() => {
    if (draftState) {
      setDensity(draftState.density);
      setHidden(
        allColumns.filter(
          (column) => !mainColumns.includes(column) && !draftState.visibleColumns.includes(column),
        ),
      );
    }
    setDraftState(null);
  }, [draftState, setDensity, setHidden, allColumns, mainColumns]);

  const isColumnVisible = useCallback(
    (column: C) => visibleColumns.includes(column),
    [visibleColumns],
  );

  const draft = draftState ?? { density, visibleColumns };

  return useMemo(
    () => ({
      density,
      primeSize: density,
      visibleColumns,
      isColumnVisible,
      showColumnsSection: allColumns.length > COLUMNS_SECTION_MIN,
      dialog: {
        isOpen: draftState !== null,
        open,
        close: cancel,
        draft,
        setDraftDensity,
        toggleDraftColumn,
        apply,
        cancel,
      },
    }),
    // `draft` deriva de `draftState`, `density` e `visibleColumns`, já listados.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      density,
      visibleColumns,
      isColumnVisible,
      allColumns.length,
      draftState,
      open,
      cancel,
      setDraftDensity,
      toggleDraftColumn,
      apply,
    ],
  );
}
